import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';

const PlacementAptitudeTestPage = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [roundId, setRoundId] = useState(null);
  const [isResuming, setIsResuming] = useState(false);
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  const session = SessionManager.getSession('candidate');

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL || FALLBACK_API_URL}/placement-training/aptitude/questions?assessment_id=${assessmentId}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions || []);
        setRoundId(data.round_id);
        setTimeRemaining(data.time_remaining_seconds || 1800);
        setIsResuming(data.is_resuming || false);

        // Pre-fill answered questions if resuming
        if (data.is_resuming && data.questions) {
          const answers = {};
          data.questions.forEach(q => {
            if (q.is_answered && q.selected_answer) {
              answers[q.id] = q.selected_answer;
            }
          });
          setSelectedAnswers(answers);
        }
      } else {
        alert('Failed to load questions. Please try again.');
        navigate(`/placement-training/assessment/${assessmentId}`);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [assessmentId, session, navigate]);

  useEffect(() => {
    if (!session) {
      navigate('/login');
      return;
    }
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Timer countdown
  useEffect(() => {
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Auto-submit when time runs out
  useEffect(() => {
    if (timeRemaining === 0 && !submitting && questions.length > 0) {
      console.log('Time up! Auto-submitting test...');
      handleSubmit();
    }
  }, [timeRemaining, submitting, questions.length]);

  // Show warning when time is running low
  useEffect(() => {
    if (timeRemaining === 60 && questions.length > 0) {
      alert('⏰ 1 minute remaining! The test will auto-submit when time runs out.');
    } else if (timeRemaining === 30 && questions.length > 0) {
      alert('⏰ 30 seconds remaining! The test will auto-submit soon.');
    }
  }, [timeRemaining, questions.length]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (questionId, answer) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleMarkForReview = (questionId) => {
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const getQuestionStatus = (question) => {
    if (selectedAnswers[question.id]) return 'answered';
    if (markedForReview.has(question.id)) return 'review';
    return 'unanswered';
  };

  const handleSubmit = async () => {
    if (submitting) return;

    try {
      setSubmitting(true);

      // Prepare answers
      const answers = questions.map((q, idx) => ({
        question_id: q.id,
        question_number: idx + 1,
        selected_answer: selectedAnswers[q.id] || '',
        time_taken_seconds: 0
      }));

      const response = await fetch(
        `${API_BASE_URL || FALLBACK_API_URL}/placement-training/aptitude/submit`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            assessment_id: assessmentId,
            round_id: roundId,
            answers
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Navigate to the next round based on backend response
        const nextRound = data.next_round;
        
        if (nextRound === 'coding') {
          navigate(`/placement-training/assessment/${assessmentId}/coding`);
        } else if (nextRound === 'tech_interview') {
          navigate(`/placement-training/assessment/${assessmentId}/tech-interview`);
        } else if (nextRound === 'hr_interview') {
          navigate(`/placement-training/assessment/${assessmentId}/hr-interview`);
        } else if (nextRound === 'completed') {
          // Assessment completed, navigate to results
          navigate(`/placement-training/assessment/${assessmentId}/results`);
        } else {
          // Fallback to coding if next_round is not specified
          navigate(`/placement-training/assessment/${assessmentId}/coding`);
        }
      } else {
        alert('Failed to submit answers. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting answers:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
      setShowSubmitDialog(false);
    }
  };

  const answeredCount = Object.keys(selectedAnswers).length;
  const unansweredCount = questions.length - answeredCount;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="quiz" size={24} className="text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Aptitude Test</h1>
                <p className="text-sm text-gray-600">
                  {answeredCount} of {questions.length} answered
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Timer */}
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                timeRemaining < 300 ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
              }`}>
                <Icon name="schedule" size={20} />
                <span className="font-mono text-lg font-bold">{formatTime(timeRemaining)}</span>
                {timeRemaining <= 60 && timeRemaining > 0 && (
                  <span className="text-xs font-medium animate-pulse">Auto-submit soon!</span>
                )}
              </div>

              {/* Submit Button */}
              <button
                onClick={() => setShowSubmitDialog(true)}
                disabled={submitting}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Test
              </button>
            </div>
          </div>

          {/* Resume Banner */}
          {isResuming && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-700">
                <Icon name="info" size={18} />
                <span className="text-sm font-medium">
                  Resuming test - Your previous answers have been restored
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Question Panel */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              {/* Question Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    Question {currentQuestion + 1} of {questions.length}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                    {currentQ?.category} - {currentQ?.subcategory}
                  </span>
                </div>

                <button
                  onClick={() => handleMarkForReview(currentQ?.id)}
                  className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium ${
                    markedForReview.has(currentQ?.id)
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon name={markedForReview.has(currentQ?.id) ? 'bookmark' : 'bookmark_border'} size={18} />
                  <span>{markedForReview.has(currentQ?.id) ? 'Marked' : 'Mark for Review'}</span>
                </button>
              </div>

              {/* Question Text */}
              <div className="mb-6">
                <p className="text-lg text-gray-900 leading-relaxed whitespace-pre-line">
                  {currentQ?.question_text}
                </p>
              </div>

              {/* Options */}
              <div className="space-y-3 mb-6">
                {['A', 'B', 'C', 'D'].map(option => (
                  <button
                    key={option}
                    onClick={() => handleAnswerSelect(currentQ?.id, option)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedAnswers[currentQ?.id] === option
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        selectedAnswers[currentQ?.id] === option
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedAnswers[currentQ?.id] === option && (
                          <Icon name="check" size={16} className="text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="font-medium text-gray-700 mr-2">{option}.</span>
                        <span className="text-gray-900">{currentQ?.[`option_${option.toLowerCase()}`]}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                <button
                  onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                  disabled={currentQuestion === 0}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="chevron_left" size={20} />
                  <span>Previous</span>
                </button>

                <button
                  onClick={() => setCurrentQuestion(prev => Math.min(questions.length - 1, prev + 1))}
                  disabled={currentQuestion === questions.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Next</span>
                  <Icon name="chevron_right" size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Question Grid Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-4">
              <h3 className="font-semibold text-gray-900 mb-4">Question Navigator</h3>

              {/* Legend */}
              <div className="space-y-2 mb-4 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span className="text-gray-600">Answered ({answeredCount})</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span className="text-gray-600">Review ({markedForReview.size})</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 bg-gray-200 rounded"></div>
                  <span className="text-gray-600">Unanswered ({unansweredCount})</span>
                </div>
              </div>

              {/* Question Grid */}
              <div className="grid grid-cols-5 gap-2">
                {questions.map((q, idx) => {
                  const status = getQuestionStatus(q);
                  return (
                    <button
                      key={q.id}
                      onClick={() => setCurrentQuestion(idx)}
                      className={`w-full aspect-square rounded-lg text-sm font-medium transition-all ${
                        currentQuestion === idx
                          ? 'ring-2 ring-blue-500 ring-offset-2'
                          : ''
                      } ${
                        status === 'answered'
                          ? 'bg-green-500 text-white hover:bg-green-600'
                          : status === 'review'
                          ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Confirmation Dialog */}
        {showSubmitDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Icon name="help_outline" size={24} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Submit Test?</h3>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Total Questions</span>
                  <span className="font-semibold text-gray-900">{questions.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-green-700">Answered</span>
                  <span className="font-semibold text-green-700">{answeredCount}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <span className="text-red-700">Unanswered</span>
                  <span className="font-semibold text-red-700">{unansweredCount}</span>
                </div>
              </div>

              {unansweredCount > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    You have {unansweredCount} unanswered question{unansweredCount > 1 ? 's' : ''}. 
                    Are you sure you want to submit?
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSubmitDialog(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlacementAptitudeTestPage;
